import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import BirdMascot from '../components/BirdMascot';

const LegalInfoPage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-muted/30 to-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <BirdMascot size={32} />
            <span className="text-xl font-heading font-bold">JailBirds</span>
          </Link>
          
          <Link to="/">
            <Button variant="outline" size="sm" className="gap-2">
              <Home className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-8"
        >
          {/* Support Contact */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-heading font-bold">Contact Support</h2>
            </div>
            <p className="text-muted-foreground mb-2">
              For questions, comments, or to contact our support team:
            </p>
            <a 
              href="mailto:support@jailbirdsgame.com" 
              className="text-lg font-semibold text-primary hover:underline"
            >
              support@jailbirdsgame.com
            </a>
          </div>

          {/* Legal Disclaimer */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h1 className="text-3xl font-heading font-bold mb-6 text-gradient-primary">
              Legal Information and Disclaimer
            </h1>

            <div className="space-y-6 text-muted-foreground leading-relaxed">
              {/* Presumption of Innocence */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  Presumption of Innocence
                </h3>
                <p>
                  <strong className="text-foreground">All individuals featured in this game are presumed innocent until proven guilty in a court of law.</strong> The arrest records and charges displayed are allegations only and do not constitute proof of guilt. An arrest does not mean that the individual has been convicted of any crime. All persons arrested or charged with a crime are innocent unless and until proven guilty beyond a reasonable doubt in a court of law.
                </p>
              </section>

              {/* Public Records */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  Public Records Information
                </h3>
                <p>
                  All arrest records, mugshots, and charges displayed in JailBirds are obtained from public records made available by the Mecklenburg County Sheriff's Office and other law enforcement agencies in North Carolina. These records are publicly accessible and are provided in accordance with North Carolina General Statutes and public records laws.
                </p>
                <p className="mt-3">
                  The information presented is sourced directly from official government databases and websites that are freely available to the public. JailBirds does not create, modify, or editorialize this information beyond what is necessary for game functionality.
                </p>
              </section>

              {/* Educational Purpose */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  Educational and Entertainment Purpose
                </h3>
                <p>
                  JailBirds is designed for educational and entertainment purposes only. The game aims to:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Increase public awareness of criminal justice processes</li>
                  <li>Educate players about different types of criminal charges</li>
                  <li>Demonstrate the transparency of public records</li>
                  <li>Provide an engaging way to learn about law enforcement activities in the community</li>
                </ul>
              </section>

              {/* No Liability */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  Accuracy and No Liability
                </h3>
                <p>
                  While we strive to present accurate information obtained from official sources, JailBirds makes no warranties or guarantees about the accuracy, completeness, or timeliness of the information displayed. Arrest records may be incomplete, outdated, or subject to change. Charges may be modified, dismissed, or adjudicated after the data is collected.
                </p>
                <p className="mt-3">
                  JailBirds and its operators are not responsible for any errors, omissions, or inaccuracies in the public records data. Users should verify any information through official channels if accuracy is critical for their purposes.
                </p>
              </section>

              {/* No Endorsement */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  No Endorsement or Judgment
                </h3>
                <p>
                  The inclusion of any individual's information in this game does not constitute an endorsement of any legal proceedings, judgment of guilt, or opinion about the individual's character or actions. JailBirds takes no position on the merits of any arrest or criminal charge displayed in the game.
                </p>
              </section>

              {/* Data Usage */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  Responsible Data Usage
                </h3>
                <p>
                  We encourage all users to:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Remember that arrests are not convictions</li>
                  <li>Treat all individuals with respect and dignity</li>
                  <li>Understand that criminal charges may be dismissed, reduced, or result in acquittal</li>
                  <li>Recognize that arrest records do not tell the complete story of any case</li>
                  <li>Refrain from using this information to harass, discriminate against, or harm any individual</li>
                </ul>
              </section>

              {/* Removal Requests */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  Record Removal or Correction
                </h3>
                <p>
                  If you believe that information displayed in JailBirds is inaccurate, outdated, or should be removed, please contact us at{' '}
                  <a href="mailto:support@jailbirdsgame.com" className="text-primary hover:underline font-semibold">
                    support@jailbirdsgame.com
                  </a>
                  . We will review all requests and work with the appropriate authorities to verify and update information as necessary.
                </p>
                <p className="mt-3">
                  Please note that we can only display information that is made available through official public records. For questions about the accuracy of arrest records or to request expungement, please contact the Mecklenburg County Sheriff's Office or the appropriate law enforcement agency directly.
                </p>
              </section>

              {/* Terms of Use */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  Terms of Use
                </h3>
                <p>
                  By using JailBirds, you agree to use the information provided solely for lawful purposes and in accordance with all applicable laws. You agree not to use this information to:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Discriminate against any individual in employment, housing, credit, or other decisions</li>
                  <li>Harass, threaten, or harm any individual</li>
                  <li>Violate any individual's right to privacy</li>
                  <li>Make decisions about credit, employment, housing, or other rights without conducting proper due diligence and following applicable laws (including the Fair Credit Reporting Act)</li>
                </ul>
              </section>

              {/* Contact Information */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  Questions or Concerns
                </h3>
                <p>
                  If you have any questions, concerns, or feedback about JailBirds or our use of public records, please contact us at:
                </p>
                <p className="mt-2">
                  <a href="mailto:support@jailbirdsgame.com" className="text-primary hover:underline font-semibold text-lg">
                    support@jailbirdsgame.com
                  </a>
                </p>
              </section>

              {/* Last Updated */}
              <section className="border-t border-border pt-6 mt-8">
                <p className="text-sm text-muted-foreground italic">
                  Last Updated: December 12, 2025
                </p>
              </section>
            </div>
          </div>

          {/* Privacy Policy */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h1 className="text-3xl font-heading font-bold mb-6 text-gradient-primary">
              Privacy Policy
            </h1>

            <div className="space-y-6 text-muted-foreground leading-relaxed">
              {/* Introduction */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  Introduction
                </h3>
                <p>
                  JailBirds ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our game and website.
                </p>
              </section>

              {/* Information We Collect */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  Information We Collect
                </h3>
                <p className="mb-3">
                  <strong className="text-foreground">Information You Provide:</strong>
                </p>
                <ul className="list-disc pl-6 space-y-1 mb-3">
                  <li><strong>Nickname:</strong> A username you create to play the game and appear on leaderboards</li>
                  <li><strong>Game Data:</strong> Your scores, games played, accuracy statistics, and leaderboard rankings</li>
                  <li><strong>Avatar Selection:</strong> Your chosen bird avatar for identification in the game</li>
                </ul>
                <p className="mb-3">
                  <strong className="text-foreground">Information Collected Automatically:</strong>
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Usage Data:</strong> Information about how you interact with the game (e.g., gameplay patterns, time spent)</li>
                  <li><strong>Device Information:</strong> Browser type, operating system, IP address, device identifiers</li>
                  <li><strong>Cookies and Similar Technologies:</strong> See "Cookies" section below</li>
                </ul>
              </section>

              {/* How We Use Your Information */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  How We Use Your Information
                </h3>
                <p className="mb-2">We use the information we collect to:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Provide and maintain the game functionality</li>
                  <li>Track and display scores on leaderboards</li>
                  <li>Personalize your gaming experience</li>
                  <li>Analyze usage patterns to improve the game</li>
                  <li>Communicate with you about the game</li>
                  <li>Display relevant advertisements</li>
                  <li>Detect, prevent, and address technical issues or fraud</li>
                </ul>
              </section>

              {/* Cookies and Tracking */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  Cookies and Tracking Technologies
                </h3>
                <p className="mb-3">
                  We use cookies and similar tracking technologies to enhance your experience. Cookies are small data files stored on your device.
                </p>
                <p className="mb-3">
                  <strong className="text-foreground">Types of Cookies We Use:</strong>
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Essential Cookies:</strong> Required for game functionality, including storing your nickname and game progress
                  </li>
                  <li>
                    <strong>Analytics Cookies:</strong> Help us understand how you use the game through Google Analytics and PostHog
                  </li>
                  <li>
                    <strong>Advertising Cookies:</strong> Used by Google AdSense to display relevant ads and measure ad performance
                  </li>
                </ul>
                <p className="mt-3">
                  You can control cookies through your browser settings. However, disabling certain cookies may limit your ability to use some features of the game.
                </p>
              </section>

              {/* Third-Party Services */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  Third-Party Services and Advertising
                </h3>
                <p className="mb-3">
                  We use third-party services that may collect information about you:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Google AdSense:</strong> Displays advertisements on our site. Google may use cookies to serve ads based on your prior visits to our site or other sites. You can opt out of personalized advertising by visiting{' '}
                    <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Google's Ads Settings
                    </a>
                    .
                  </li>
                  <li>
                    <strong>Google Analytics:</strong> Helps us analyze website traffic and usage patterns. Learn more at{' '}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Google's Privacy Policy
                    </a>
                    .
                  </li>
                  <li>
                    <strong>PostHog:</strong> Provides analytics to help us understand user behavior and improve the game.
                  </li>
                </ul>
              </section>

              {/* Data Sharing */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  How We Share Your Information
                </h3>
                <p className="mb-3">
                  We do not sell your personal information. We may share your information in the following circumstances:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Publicly:</strong> Your nickname, avatar, and scores are displayed on public leaderboards</li>
                  <li><strong>With Service Providers:</strong> Third-party companies that help us operate the game (e.g., hosting, analytics, advertising)</li>
                  <li><strong>For Legal Purposes:</strong> When required by law or to protect our rights</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, sale, or acquisition</li>
                </ul>
              </section>

              {/* Data Retention */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  Data Retention
                </h3>
                <p>
                  We retain your game data (nickname, scores, statistics) for as long as you continue to use the game or as needed to provide our services. You may request deletion of your data by contacting us at{' '}
                  <a href="mailto:support@jailbirdsgame.com" className="text-primary hover:underline font-semibold">
                    support@jailbirdsgame.com
                  </a>
                  .
                </p>
              </section>

              {/* Your Rights */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  Your Privacy Rights
                </h3>
                <p className="mb-2">Depending on your location, you may have the following rights:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                  <li><strong>Opt-Out:</strong> Opt out of certain data collection and advertising</li>
                  <li><strong>Data Portability:</strong> Request your data in a portable format</li>
                </ul>
                <p className="mt-3">
                  To exercise these rights, contact us at{' '}
                  <a href="mailto:support@jailbirdsgame.com" className="text-primary hover:underline font-semibold">
                    support@jailbirdsgame.com
                  </a>
                  .
                </p>
              </section>

              {/* Children's Privacy */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  Children's Privacy
                </h3>
                <p>
                  JailBirds is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
                </p>
              </section>

              {/* Data Security */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  Data Security
                </h3>
                <p>
                  We implement reasonable security measures to protect your information. However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
                </p>
              </section>

              {/* International Users */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  International Users
                </h3>
                <p>
                  If you are accessing JailBirds from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States where our servers are located. By using the game, you consent to this transfer.
                </p>
              </section>

              {/* Changes to Privacy Policy */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  Changes to This Privacy Policy
                </h3>
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  Contact Us
                </h3>
                <p>
                  If you have questions about this Privacy Policy or our data practices, please contact us at:
                </p>
                <p className="mt-2">
                  <a href="mailto:support@jailbirdsgame.com" className="text-primary hover:underline font-semibold text-lg">
                    support@jailbirdsgame.com
                  </a>
                </p>
              </section>

              {/* Last Updated */}
              <section className="border-t border-border pt-6 mt-8">
                <p className="text-sm text-muted-foreground italic">
                  Last Updated: December 12, 2025
                </p>
              </section>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border bg-card/50 mt-16 py-8">
        <div className="container mx-auto px-4 text-center">
          <Link to="/">
            <Button variant="outline" size="sm" className="gap-2 mb-4">
              <Home className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
          <p className="text-sm text-muted-foreground">© 2025 JailBirds. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LegalInfoPage;
